import { useEffect, useState, type ReactNode } from 'react';
import { api, formatBytes, type Camera } from '../lib/api';
import { useBahasa } from '../i18n';

interface Check {
  ok: boolean;
  detail: string;
}

interface Hwaccel {
  backend: string;
  decode: boolean;
  encode: boolean;
}

/** `null` = tidak ada GPU NVIDIA — kartunya tidak dirender sama sekali. */
interface Gpu {
  name: string;
  driver: string | null;
  memoryUsedMb: number | null;
  memoryTotalMb: number | null;
  temperatureC: number | null;
  utilizationPct: number | null;
  decoderPct: number | null;
  powerW: number | null;
  powerLimitW: number | null;
}

interface Cpu {
  model: string;
  cores: number;
  usagePct: number | null;
  load1: number;
  load5: number;
  load15: number;
  memUsedMb: number | null;
  memTotalMb: number | null;
  uptimeSeconds: number;
  ffmpegProcesses: number | null;
}

interface Health {
  status: string;
  version: string;
  env: string;
  hwaccel: Hwaccel;
  uptimeSeconds: number;
  checks: Record<string, Check>;
}

const CHECK_LABEL: Record<string, string> = {
  database: 'Database (MariaDB)',
  redis: 'Redis',
  worker: 'Recording worker',
  recVolume: 'Recording storage',
};

/**
 * Nama backend saja tidak cukup: decode dan encode bisa berbeda nasib, dan
 * decode-lah bagian termahalnya (§D4.1).
 */
function describeHwaccel(hw: Hwaccel): string {
  if (hw.decode && hw.encode) return `${hw.backend} — full GPU`;
  if (hw.decode) return `${hw.backend} — GPU decode, CPU encode`;
  if (hw.encode) return `${hw.backend} — GPU encode, CPU decode`;
  return `${hw.backend} — CPU only`;
}

/** "412383s" tidak memberi tahu siapa pun berapa lama server sudah menyala. */
function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

/**
 * Angka besar dengan label. Warna hanya penegas — angkanya sendiri, dan label
 * di bawahnya, yang membawa artinya (§G3.1).
 */
function Stat({ label, value, tone }: { label: string; value: number; tone?: 'good' | 'warn' | 'bad' }) {
  const color =
    tone === 'good'
      ? 'text-green-600'
      : tone === 'warn'
        ? 'text-amber-500'
        : tone === 'bad'
          ? 'text-red-600'
          : '';
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3">
      <div className={`text-2xl font-semibold tabular-nums ${value === 0 && tone ? 'text-muted' : color}`}>
        {value}
      </div>
      <div className="mt-0.5 text-sm text-muted">{label}</div>
    </div>
  );
}

/** Kartu bertajuk — kepalanya sengaja sama dengan `thead` tabel komponen. */
function Kartu({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-line bg-surface">
      <h2 className="border-b border-line bg-subtle px-4 py-2 text-sm font-medium text-muted">{title}</h2>
      <dl className="text-sm">{children}</dl>
    </section>
  );
}

/**
 * Satu baris kartu. `pct` menambah bilah tipis di bawah nilainya — bilah itu
 * pengulangan angka yang sudah tertulis, bukan pengganti (§G3.1), jadi layar
 * pembaca cukup membaca teksnya dan bilahnya `aria-hidden`.
 */
function Baris({ label, value, pct }: { label: string; value: ReactNode; pct?: number | null }) {
  const warna = pct == null ? '' : pct >= 90 ? 'bg-red-600' : pct >= 75 ? 'bg-amber-500' : 'bg-accent';
  return (
    <div className="border-t border-line px-4 py-2 first:border-t-0">
      <div className="flex items-baseline justify-between gap-4">
        <dt className="shrink-0 text-muted">{label}</dt>
        <dd className="text-right tabular-nums">{value}</dd>
      </div>
      {pct != null && (
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-line" aria-hidden="true">
          <div className={`h-full ${warna}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
        </div>
      )}
    </div>
  );
}

export function StatusPage() {
  const { t } = useBahasa();
  const [health, setHealth] = useState<Health | null>(null);
  const [cameras, setCameras] = useState<Camera[] | null>(null);
  const [cpu, setCpu] = useState<Cpu | null>(null);
  const [gpu, setGpu] = useState<Gpu | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch('/healthz');
        const body = (await res.json()) as Health;
        if (alive) {
          setHealth(body);
          setError(null);
        }
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
      try {
        const r = await api<{ cameras: Camera[] }>('/cameras');
        if (alive) setCameras(r.cameras);
      } catch {
        /* Daftar kamera opsional di sini — status komponen tetap ditampilkan. */
      }
      try {
        const r = await api<{ cpu: Cpu | null; gpu: Gpu | null }>('/system/host');
        if (alive) {
          setCpu(r.cpu);
          setGpu(r.gpu);
        }
      } catch {
        /* Sama seperti kamera: telemetri host pelengkap, bukan syarat. */
      }
    };
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const online = cameras?.filter((c) => c.status === 'online').length ?? 0;
  const offline = cameras?.filter((c) => c.status === 'offline').length ?? 0;
  const faulty = cameras?.filter((c) => c.status === 'error').length ?? 0;
  const disabled = cameras?.filter((c) => !c.isEnabled).length ?? 0;

  const memPct =
    cpu && cpu.memUsedMb !== null && cpu.memTotalMb
      ? Math.round((cpu.memUsedMb / cpu.memTotalMb) * 100)
      : null;
  const vramPct =
    gpu && gpu.memoryUsedMb !== null && gpu.memoryTotalMb
      ? Math.round((gpu.memoryUsedMb / gpu.memoryTotalMb) * 100)
      : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <h1 className="text-2xl">{t('System status')}</h1>

      {error && (
        <p className="mt-6 rounded-md border border-line bg-subtle px-3 py-2 text-sm" role="alert">
          {t('Cannot reach the API: {pesan}', { pesan: error })}
        </p>
      )}

      {cameras && (
        <section className="mt-8">
          <h2 className="text-sm font-medium text-muted">{t('Cameras')}</h2>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t('Total')} value={cameras.length} />
            <Stat label={t('Online')} value={online} tone="good" />
            <Stat label={t('Offline')} value={offline} tone="warn" />
            <Stat label={t('Faulty')} value={faulty} tone="bad" />
          </div>
          {disabled > 0 && (
            <p className="mt-2 text-xs text-muted">
              {t('{n} disabled', { n: disabled })}
            </p>
          )}
        </section>
      )}

      {health && (
        <section className="mt-8 overflow-hidden rounded-lg border border-line">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">{t('Lensa component status')}</caption>
            <thead className="bg-subtle">
              <tr>
                <th scope="col" className="px-3 py-2 font-normal">
                  {t('Component')}
                </th>
                <th scope="col" className="px-3 py-2 font-normal">
                  {t('Status')}
                </th>
                <th scope="col" className="px-3 py-2 font-normal">
                  {t('Details')}
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(health.checks).map(([key, check]) => (
                <tr key={key} className="border-t border-line">
                  <th scope="row" className="px-3 py-2 font-normal">
                    {t(CHECK_LABEL[key] ?? key)}
                  </th>
                  <td className="px-3 py-2">
                    {/* Status tidak pernah hanya lewat warna (§G3.1): selalu ada
                        label teks di samping titiknya. */}
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className={`inline-block h-2.5 w-2.5 rounded-full ${
                          check.ok ? 'bg-green-600' : 'bg-amber-500'
                        }`}
                      />
                      {check.ok ? t('OK') : t('Needs attention')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted">{check.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Tiga kartu sejajar: perangkat lunaknya, lalu mesin yang memikulnya.
          Kartu GPU hilang — bukan kosong — kalau tidak ada GPU NVIDIA: deretan
          "—" untuk hardware yang tidak terpasang cuma bikin orang bertanya. */}
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {health && (
          <Kartu title={t('Service')}>
            <Baris label={t('Version')} value={health.version} />
            <Baris label={t('Environment')} value={health.env} />
            <Baris label={t('Hardware acceleration')} value={describeHwaccel(health.hwaccel)} />
            <Baris label={t('API uptime')} value={formatUptime(health.uptimeSeconds)} />
          </Kartu>
        )}

        {cpu && (
          <Kartu title={t('CPU')}>
            <Baris label={t('Processor')} value={`${cpu.model} · ${t('{n} cores', { n: cpu.cores })}`} />
            <Baris
              label={t('Usage')}
              value={cpu.usagePct !== null ? `${cpu.usagePct}%` : '—'}
              pct={cpu.usagePct}
            />
            <Baris
              label={t('Memory')}
              value={
                cpu.memUsedMb !== null && cpu.memTotalMb !== null
                  ? `${formatBytes(cpu.memUsedMb * 1048576)} / ${formatBytes(cpu.memTotalMb * 1048576)}${
                      memPct !== null ? ` (${memPct}%)` : ''
                    }`
                  : '—'
              }
              pct={memPct}
            />
            {/* Load average dibaca bertiga atau tidak sama sekali: satu angka
                tidak memberi tahu arah — naik, turun, atau sudah lama begitu. */}
            <Baris
              label={t('Load average')}
              value={`${cpu.load1.toFixed(2)} · ${cpu.load5.toFixed(2)} · ${cpu.load15.toFixed(2)}`}
            />
            <Baris
              label={t('ffmpeg processes')}
              value={cpu.ffmpegProcesses !== null ? cpu.ffmpegProcesses : '—'}
            />
            <Baris label={t('Host uptime')} value={formatUptime(cpu.uptimeSeconds)} />
          </Kartu>
        )}

        {gpu && (
          <Kartu title={t('GPU')}>
            <Baris
              label={t('Card')}
              value={gpu.driver ? `${gpu.name} — ${t('driver')} ${gpu.driver}` : gpu.name}
            />
            <Baris
              label={t('Memory')}
              value={
                gpu.memoryUsedMb !== null && gpu.memoryTotalMb !== null
                  ? t('{dipakai} MB of {total} MB used ({pct}%)', {
                      dipakai: gpu.memoryUsedMb,
                      total: gpu.memoryTotalMb,
                      pct: vramPct ?? 0,
                    })
                  : '—'
              }
              pct={vramPct}
            />
            <Baris
              label={t('Utilization')}
              value={t('{gpu}% GPU, {dec}% decoder', {
                gpu: gpu.utilizationPct ?? '—',
                dec: gpu.decoderPct ?? '—',
              })}
              pct={gpu.utilizationPct}
            />
            <Baris
              label={t('Temperature')}
              value={gpu.temperatureC !== null ? `${gpu.temperatureC} °C` : '—'}
            />
            <Baris
              label={t('Power')}
              value={
                gpu.powerW !== null
                  ? `${gpu.powerW} W${gpu.powerLimitW !== null ? ` / ${gpu.powerLimitW} W` : ''}`
                  : '—'
              }
            />
          </Kartu>
        )}
      </div>
    </div>
  );
}
